import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || '';

const V = {
  ink:    '#0c1830', ink2: '#2a3956', ink3: '#5a6680', ink4: '#8a93a6',
  paper:  '#fbfaf6', paper2: '#f3f1ea', paper3: '#e6e2d5', line: '#d8d3c2',
  gold:   '#c8a64b', good: '#2a6f4f', goodSoft: '#e8f0eb', bad: '#9c2b1a', badSoft: '#f4e8e5',
  warn:   '#a8731a', warnSoft: '#f5ecd9',
  serif:  "'Source Serif 4', Georgia, serif",
  sans:   "'Inter', -apple-system, system-ui, sans-serif",
  mono:   "'JetBrains Mono', ui-monospace, monospace",
};

function FontLoader() {
  useEffect(() => {
    if (document.getElementById('verdict-fonts')) return;
    const link = document.createElement('link');
    link.id = 'verdict-fonts';
    link.href = 'https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,400;8..60,500;8..60,600&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);
  return null;
}

function formatValue(val, cur) {
  if (val == null) return '—';
  const sym = cur === 'USD' ? '$' : cur === 'GBP' ? '£' : '€';
  return `${sym}${Number(val).toLocaleString('en-GB')}`;
}

// ── Number parser — handles "1,600" (thousands) vs "19.50" (decimal) ─────────
function parseAmount(numStr) {
  if (!numStr) return null;
  const s = String(numStr).trim().replace(/\s/g, '');
  if (/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(s)) return parseFloat(s.replace(/,/g, ''));
  if (/^\d+\.\d+$/.test(s)) return parseFloat(s);
  if (/^\d+$/.test(s))      return parseFloat(s);
  return parseFloat(s.replace(/,/g, '')) || null;
}

// ── EUR→USD display conversion ────────────────────────────────────────────────
// Returns { primary: "≈ $X", secondary: original } for EUR cell values
function toUsdDisplay(str, rate) {
  if (!rate || !str || str === '—') return null;
  const s = String(str);
  if (!/EUR|€/.test(s)) return null;
  let num = null;
  const afterMatch = s.match(/(?:EUR|€)\s*([\d][0-9,.]*)/);
  if (afterMatch) num = parseAmount(afterMatch[1]);
  if (!num) {
    const beforeMatch = s.match(/([\d][0-9,.]*)\s*(?:EUR|€)/);
    if (beforeMatch) num = parseAmount(beforeMatch[1]);
  }
  if (!num || num <= 0) return null;
  const unitMatch = s.match(/\b(each|per\s+unit|per\s+pcs|\/unit)\b/i);
  const suffix = unitMatch ? ` ${unitMatch[1]}` : '';
  return {
    primary:   `≈ $${Math.round(num * rate).toLocaleString('en-GB')}${suffix}`,
    secondary: s
  };
}

// ── Spread / delta computation ────────────────────────────────────────────────
function isPerUnit(str) {
  return /\b(pcs|each|sets|units|pc|item|items)\b/i.test(String(str || ''));
}
function extractFirstNumber(str) {
  if (!str || str === '—') return null;
  const clean = String(str).replace(/^[✓≈~\s]+/, '').replace(/[$€£,]/g, '');
  const m = clean.match(/\d+(\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
}
function computeDelta(values, rate) {
  const strs = (values || []).filter(v => v && v !== '—');
  if (strs.length < 2) return null;
  const hasPerUnit = strs.some(isPerUnit);
  const hasLump    = strs.some(v => !isPerUnit(v) && extractFirstNumber(v) !== null);
  if (hasPerUnit && hasLump) return 'mismatch';
  const nums = strs.map(str => {
    const s = String(str);
    if (rate && /EUR|€/.test(s)) {
      const m = s.match(/(?:EUR|€)\s*([\d][0-9,.]*)/);
      if (m) { const n = parseAmount(m[1]); if (n > 0) return n * rate; }
    }
    return extractFirstNumber(str);
  }).filter(n => n !== null && n > 0);
  if (nums.length < 2) return null;
  const min = Math.min(...nums), max = Math.max(...nums);
  if (min === 0) return null;
  const pct = Math.round(((max - min) / min) * 100);
  return pct > 5 ? pct : null;
}

const CAT_COLORS = {
  commercial: { color: '#1a4d8f', bg: '#dbeafe' },
  scope:      { color: V.good,    bg: V.goodSoft },
  terms:      { color: V.warn,    bg: V.warnSoft },
  safety:     { color: '#5b21b6', bg: '#ede9fe' },
};

export default function Compare() {
  const { id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const token = localStorage.getItem('clarix_token');

  const [comparison, setComparison]       = useState([]);
  const [aiInsight, setAiInsight]         = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [compRows, setCompRows]           = useState([]);
  const [bestId, setBestId]               = useState(null);
  const [worstId, setWorstId]             = useState(null);
  const [project, setProject]             = useState(null);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [generating, setGenerating]       = useState(false);
  const [reanalyzing, setReanalyzing]     = useState(false);
  const [isCached, setIsCached]           = useState(false);
  const [cachedAt, setCachedAt]           = useState(null);
  const [fxRate, setFxRate]               = useState(null);
  const [fxDate, setFxDate]               = useState('');

  // Fetch EUR→USD rate once on mount
  useEffect(() => {
    fetch(`${API}/api/fx/rate`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.rate) { setFxRate(d.rate); setFxDate(d.updated || ''); } })
      .catch(() => {});
  }, [token]);

  const runComparison = (forceRefresh = false) => {
    const docIds = searchParams.get('docs');
    if (!docIds) { setError('No documents selected.'); setLoading(false); return; }
    const ids = docIds.split(',').map(Number).filter(Boolean);
    if (ids.length < 2) { setError('Select at least 2 documents.'); setLoading(false); return; }

    if (forceRefresh) {
      setReanalyzing(true);
    } else {
      setLoading(true);
    }
    setError('');

    Promise.all([
      fetch(`${API}/api/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API}/api/documents/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ document_ids: ids, force_refresh: forceRefresh })
      }).then(r => r.json())
    ])
      .then(([projData, cmpData]) => {
        if (projData.project) setProject(projData.project);
        if (cmpData.error) { setError(cmpData.error); return; }
        setComparison(cmpData.comparison || []);
        setBestId(cmpData.bestId);
        setWorstId(cmpData.worstId);
        setIsCached(cmpData.cached === true);
        setCachedAt(cmpData.cached_at || null);
        if (typeof cmpData.aiInsight === 'string') {
          setAiInsight(cmpData.aiInsight); setCompRows([]);
        } else if (cmpData.aiInsight && typeof cmpData.aiInsight === 'object') {
          setAiInsight(cmpData.aiInsight.insight || '');
          setRecommendation(cmpData.aiInsight.recommendation || '');
          setCompRows(cmpData.aiInsight.comparison_rows || []);
        }
      })
      .catch(() => setError('Failed to load comparison.'))
      .finally(() => { setLoading(false); setReanalyzing(false); });
  };

  useEffect(() => {
    runComparison(false);
  }, [projectId, searchParams, token]);

  const handleGenerateReport = async () => {
    const docIds = searchParams.get('docs');
    if (!docIds) return;
    const ids = docIds.split(',').map(Number).filter(Boolean);
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/reports/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id: parseInt(projectId), document_ids: ids, type: 'word' })
      });
      const data = await res.json();
      const dlRes = await fetch(`${API}/api/reports/download/${encodeURIComponent(data.filename)}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await dlRes.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = data.filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error('Report error:', err); }
    finally { setGenerating(false); }
  };

  const categories = ['all', ...new Set((compRows || []).map(r => r.category).filter(Boolean))];
  const visibleRows = activeCategory === 'all' ? compRows : compRows.filter(r => r.category === activeCategory);

  // FX derived
  const currencies      = [...new Set(comparison.map(d => d.currency).filter(Boolean))];
  const mixedCurrencies = currencies.length > 1;
  const showFx          = mixedCurrencies && currencies.includes('EUR') && !!fxRate;

  // Scope coverage per document column
  const scopeRows = (compRows || []).filter(r => r.category === 'scope');
  const coverageByIndex = comparison.map((_, vi) => {
    if (scopeRows.length === 0) return null;
    const covered = scopeRows.filter(r => { const v = (r.values || [])[vi]; return v && v !== '—'; }).length;
    return { covered, total: scopeRows.length };
  });

  const metadataRows = [
    { label: 'Supplier',      fn: d => d.supplier || '—' },
    { label: 'Type',          fn: d => d.document_type || '—' },
    { label: 'Value',         fn: d => formatValue(d.total_value, d.currency), isValue: true },
    { label: 'Date',          fn: d => d.document_date || '—' },
    { label: 'Validity',      fn: d => d.validity_date || '—' },
    { label: 'Payment Terms', fn: d => d.payment_terms || '—' },
    { label: 'Incoterms',     fn: d => d.incoterms || '—' },
    { label: 'Status',        fn: d => d.awarded ? 'Awarded ✓' : 'Not awarded' },
  ];

  const NavChrome = () => (
    <>
      <div style={s.utilBar}>
        <div style={s.utilDot}></div>
        <span style={s.utilLabel}>LIVE INTEL</span>
        <span style={{ fontSize: 11, color: 'rgba(251,250,246,0.5)', fontFamily: V.sans }}>The Negotiation Room · {comparison.length} suppliers compared</span>
      </div>
      <div style={s.nav}>
        <div style={s.wordmark} onClick={() => navigate('/')}><span style={{ color: V.gold }}>V</span>erdict</div>
        <div style={s.navDivider}></div>
        <div style={s.navTag}>Negotiation Intelligence</div>
        <div style={s.navItems}>
          {['Negotiations','The Room','Suppliers','Documents','Reports'].map(item => (
            <div key={item} style={{ ...s.navItem, color: item === 'Negotiations' ? V.ink : V.ink3, borderBottom: item === 'Negotiations' ? `2px solid ${V.gold}` : '2px solid transparent' }}
              onClick={() => {
                if (item === 'Negotiations') navigate('/');
                if (item === 'Suppliers') navigate('/suppliers');
              }}>{item}</div>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          {user && <span style={{ fontSize: 12, color: V.ink4 }}>{user.email}</span>}
          {logout && <button style={s.signOut} onClick={logout}>Sign out</button>}
        </div>
      </div>
      <div style={s.breadBar}>
        <span style={s.breadLink} onClick={() => navigate('/')}>Negotiations</span>
        <span style={s.breadSep}>/</span>
        {project && <span style={s.breadLink} onClick={() => navigate(`/projects/${projectId}`)}>{project.name}</span>}
        {project && <span style={s.breadSep}>/</span>}
        <span style={s.breadCurrent}>The Negotiation Room</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button style={s.btnGhost} onClick={() => navigate(`/projects/${projectId}/documents`)}>← Evidence Locker</button>
          <button style={{ ...s.btnGhost, color: V.good, borderColor: V.good }} onClick={handleGenerateReport} disabled={generating}>
            {generating ? 'Generating…' : '↓ Generate Report'}
          </button>
        </div>
      </div>
    </>
  );

  if (loading) return (
    <div style={s.wrap}>
      <FontLoader />
      <NavChrome />
      <div style={s.loadWrap}>
        <div style={{ fontSize: 32, color: V.gold, marginBottom: 16 }}>◎</div>
        <div style={{ fontFamily: V.serif, fontSize: 22, fontWeight: 400, color: V.ink, marginBottom: 8 }}>Consigliere is reading your documents…</div>
        <div style={{ fontSize: 13, color: V.ink4, fontFamily: V.sans }}>Extracting and comparing content — this takes a few seconds</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={s.wrap}>
      <FontLoader />
      <NavChrome />
      <div style={s.loadWrap}>
        <div style={{ fontFamily: V.serif, fontSize: 20, color: V.bad, marginBottom: 8 }}>Unable to compare</div>
        <div style={{ fontSize: 13, color: V.ink4, fontFamily: V.sans, marginBottom: 20 }}>{error}</div>
        <button style={s.btnGold} onClick={() => navigate(`/projects/${projectId}/documents`)}>← Back to Evidence Locker</button>
      </div>
    </div>
  );

  return (
    <div style={s.wrap}>
      <FontLoader />
      <NavChrome />

      <div style={s.body}>

        {/* Page heading */}
        <div style={s.pageHead}>
          <div>
            <div style={s.pageEyebrow}>DOCUMENT ANALYSIS · {comparison.length} DOCUMENTS</div>
            <div style={s.pageTitle}>
              {compRows.length > 0
                ? `${compRows.length} items on the table.`
                : `${comparison.length} bids on the table.`}
            </div>
            {project && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                <div style={s.pageSub}>{project.name} · AI-prepared comparison</div>
                {isCached && cachedAt && (
                  <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: V.good, background: V.goodSoft, padding: '2px 8px', borderRadius: 3, fontFamily: V.mono }}>
                    CACHED · {new Date(cachedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                )}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignSelf: 'flex-start' }}>
            <button
              style={{ ...s.btnGhost, opacity: reanalyzing ? 0.6 : 1 }}
              onClick={() => runComparison(true)}
              disabled={reanalyzing}
              title="Request a fresh AI analysis, bypassing the cache"
            >
              {reanalyzing ? '◎ Reanalysing…' : '↺ Reanalyse'}
            </button>
            <button style={s.btnGold} onClick={handleGenerateReport} disabled={generating}>
              {generating ? 'Generating…' : '↓ Generate Report'}
            </button>
          </div>
        </div>

        {/* Reanalyzing overlay strip */}
        {reanalyzing && (
          <div style={{ background: '#fdf8ee', border: `1px solid ${V.gold}`, borderRadius: 4, padding: '10px 16px', fontSize: 12, color: V.warn, fontFamily: V.sans, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>◎</span>
            Consigliere is re-reading your documents — fresh analysis in progress…
          </div>
        )}

        {/* AI Insight */}
        {aiInsight && (
          <div style={s.insightBar}>
            <div style={s.insightLeft}>
              <div style={s.insightIcon}>◎</div>
              <div style={s.insightLabel}>AI INSIGHT</div>
            </div>
            <div style={{ fontSize: 13, color: V.ink2, lineHeight: 1.65, fontFamily: V.sans }}>{aiInsight}</div>
          </div>
        )}

        {/* Recommendation */}
        {recommendation && (
          <div style={{ ...s.insightBar, borderLeftColor: V.gold, background: '#fdf8ee' }}>
            <div style={s.insightLeft}>
              <div style={{ ...s.insightIcon, background: '#fdf0d0', border: `1px solid ${V.gold}`, color: V.gold }}>★</div>
              <div style={{ ...s.insightLabel, color: V.gold }}>RECOMMENDATION</div>
            </div>
            <div style={{ fontSize: 13, color: V.ink2, lineHeight: 1.65, fontFamily: V.sans }}>{recommendation}</div>
          </div>
        )}

        {/* Mixed currency banner */}
        {mixedCurrencies && (
          <div style={{ background: V.warnSoft, border: `1px solid ${V.warn}`, borderLeft: `3px solid ${V.warn}`, borderRadius: 4, padding: '10px 16px', fontSize: 12, color: V.warn, lineHeight: 1.6, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span style={{ flexShrink: 0, fontSize: 14 }}>⚠</span>
            <span>
              <strong>Mixed currencies detected</strong> ({currencies.join(', ')}).{' '}
              {showFx
                ? <>EUR values shown as <strong>USD equivalents</strong> at live rate: <strong>1 EUR = {fxRate.toFixed(4)} USD</strong>{fxDate ? ` · Rate updated ${fxDate}` : ''}. Original EUR prices shown in grey.</>
                : <>Price comparisons may not be directly comparable — verify FX rates before awarding.</>}
            </span>
          </div>
        )}

        {/* Document cards */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(comparison.length, 4)}, 1fr)`, gap: 12 }}>
          {comparison.map((doc, vi) => {
            const isBest  = doc.id === bestId;
            const isWorst = doc.id === worstId;
            const cov     = coverageByIndex[vi];
            const isEur   = doc.currency === 'EUR' && showFx && doc.total_value;
            const cardPrimary   = isEur
              ? `≈ $${Math.round(doc.total_value * fxRate).toLocaleString('en-GB')}`
              : doc.total_value ? formatValue(doc.total_value, doc.currency) : null;
            const cardSecondary = isEur ? formatValue(doc.total_value, 'EUR') : null;
            return (
              <div key={doc.id} style={{ ...s.docCard, borderTop: `3px solid ${isBest ? V.good : isWorst ? V.bad : V.line}` }}>
                {isBest  && <div style={s.pillBest}>BEST VALUE</div>}
                {isWorst && <div style={s.pillWorst}>HIGHEST COST</div>}
                {!isBest && !isWorst && <div style={{ height: 20 }}></div>}
                <div style={{ fontFamily: V.mono, fontSize: 11, color: V.ink3, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.original_name}</div>
                <div style={{ fontFamily: V.sans, fontSize: 12, fontWeight: 500, color: V.ink2 }}>{doc.supplier || '—'}</div>
                <div style={{ fontSize: 11, color: V.ink4, marginTop: 2 }}>{doc.document_date || '—'}</div>
                {cardPrimary && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontFamily: V.mono, fontSize: 18, fontWeight: 500, color: isBest ? V.good : isWorst ? V.bad : V.ink }}>
                      {cardPrimary}
                    </div>
                    {cardSecondary && <div style={{ fontFamily: V.mono, fontSize: 11, color: V.ink4, marginTop: 2 }}>{cardSecondary} quoted</div>}
                  </div>
                )}
                {cov && cov.total > 0 && (
                  <div style={{
                    marginTop: 6, padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                    display: 'inline-block', alignSelf: 'flex-start',
                    background: cov.covered === cov.total ? V.goodSoft : cov.covered >= cov.total * 0.7 ? V.warnSoft : V.badSoft,
                    color:      cov.covered === cov.total ? V.good      : cov.covered >= cov.total * 0.7 ? V.warn      : V.bad,
                  }}>{cov.covered}/{cov.total} scope items</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Category filter */}
        {categories.length > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {categories.map(cat => (
              <button key={cat} style={{ ...s.catTab, ...(activeCategory === cat ? s.catTabOn : {}) }} onClick={() => setActiveCategory(cat)}>
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>
        )}

        {/* Comparison table */}
        <div style={s.tableWrap}>
          {showFx && (
            <div style={{ padding: '8px 16px', fontSize: 11, color: V.warn, background: V.warnSoft, borderBottom: `1px solid ${V.line}`, fontStyle: 'italic' }}>
              EUR values shown as USD equivalents · 1 EUR = {fxRate.toFixed(4)} USD · {fxDate} · Original EUR prices shown below each value
            </div>
          )}
          <table style={s.table}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${V.line}`, background: V.paper2 }}>
                <th style={{ ...s.th, width: 220 }}>Item</th>
                {comparison.map(doc => (
                  <th key={doc.id} style={{ ...s.th, background: doc.id === bestId ? V.goodSoft : doc.id === worstId ? V.badSoft : V.paper2, color: doc.id === bestId ? V.good : doc.id === worstId ? V.bad : V.ink3 }}>
                    {doc.supplier || doc.original_name}
                    {showFx && doc.currency === 'EUR' && <div style={{ fontSize: 8, fontWeight: 400, letterSpacing: 0, textTransform: 'none', color: V.ink4, marginTop: 2 }}>values in USD equiv.</div>}
                  </th>
                ))}
                {comparison.length > 1 && (
                  <th style={{ ...s.th, textAlign: 'center' }}>
                    Spread
                    {showFx && <div style={{ fontSize: 8, fontWeight: 400, letterSpacing: 0, textTransform: 'none', color: V.ink4, marginTop: 2 }}>USD-normalised</div>}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {(compRows.length > 0 ? visibleRows : metadataRows).map((row, ri) => {
                const catCfg = CAT_COLORS[row.category] || CAT_COLORS.commercial;
                const values = compRows.length > 0 ? (row.values || []) : comparison.map(doc => row.fn(doc));
                const delta  = computeDelta(values, showFx ? fxRate : null);
                return (
                  <tr key={ri} style={{ background: ri % 2 === 0 ? V.paper : V.paper2, borderBottom: `0.5px solid ${V.line}` }}>
                    <td style={s.tdLabel}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {row.category && <span style={{ width: 6, height: 6, borderRadius: '50%', background: catCfg.color, flexShrink: 0 }}></span>}
                        <span>{row.label}</span>
                        {row.category && <span style={{ fontSize: 8, color: catCfg.color, background: catCfg.bg, padding: '1px 5px', borderRadius: 3, textTransform: 'uppercase', letterSpacing: 0.5 }}>{row.category}</span>}
                      </div>
                    </td>
                    {values.map((val, vi) => {
                      const doc         = comparison[vi];
                      const isBestCell  = doc?.id === bestId  && (row.category === 'commercial' || row.isValue);
                      const isWorstCell = doc?.id === worstId && (row.category === 'commercial' || row.isValue);
                      const isBestVal   = val && String(val).startsWith('✓ ');
                      const usdDisplay  = showFx ? toUsdDisplay(val, fxRate) : null;
                      return (
                        <td key={vi} style={{ ...s.tdValue, background: isBestCell ? V.goodSoft : isWorstCell ? V.badSoft : 'inherit' }}>
                          {usdDisplay ? (
                            <>
                              <div style={{ color: V.ink2, fontWeight: 500 }}>{usdDisplay.primary}</div>
                              <div style={{ fontSize: 10, color: V.ink4, fontFamily: V.mono, marginTop: 3 }}>{usdDisplay.secondary}</div>
                            </>
                          ) : (
                            <div style={{ color: isBestVal || isBestCell ? V.good : isWorstCell ? V.bad : V.ink2, fontWeight: isBestCell || isBestVal ? 600 : 400 }}>
                              {val || '—'}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    {comparison.length > 1 && (
                      <td style={{ ...s.tdValue, textAlign: 'center', fontFamily: V.mono, fontSize: 11 }}>
                        {delta === 'mismatch'
                          ? <span style={{ background: V.paper3, color: V.ink4, padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700 }} title="Unit bases differ">~</span>
                          : delta !== null
                            ? <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: delta > 50 ? V.badSoft : delta > 20 ? V.warnSoft : V.paper3, color: delta > 50 ? V.bad : delta > 20 ? V.warn : V.ink3 }}>+{delta}%</span>
                            : <span style={{ color: V.line }}>—</span>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {showFx && (
            <div style={{ padding: '8px 16px', fontSize: 10, color: V.ink4, borderTop: `1px solid ${V.line}`, background: V.paper2, fontStyle: 'italic' }}>
              ¹ USD equivalents at 1 EUR = {fxRate.toFixed(4)} USD · Rate updated {fxDate} · For reference only
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

const s = {
  wrap:        { minHeight: '100vh', background: V.paper2, fontFamily: V.sans, color: V.ink, display: 'flex', flexDirection: 'column' },
  utilBar:     { background: V.ink, height: 32, display: 'flex', alignItems: 'center', padding: '0 24px', gap: 12, flexShrink: 0 },
  utilDot:     { width: 6, height: 6, borderRadius: '50%', background: V.gold },
  utilLabel:   { fontSize: 9, fontWeight: 700, letterSpacing: 2, color: V.gold, textTransform: 'uppercase' },
  nav:         { background: V.paper, borderBottom: `1px solid ${V.line}`, height: 60, display: 'flex', alignItems: 'center', padding: '0 24px', flexShrink: 0 },
  wordmark:    { fontFamily: V.serif, fontSize: 26, fontWeight: 600, letterSpacing: -0.5, color: V.ink, cursor: 'pointer' },
  navDivider:  { width: 1, height: 18, background: V.line, margin: '0 12px' },
  navTag:      { fontSize: 10, fontWeight: 500, letterSpacing: 2.5, textTransform: 'uppercase', color: V.ink3, marginRight: 32, whiteSpace: 'nowrap' },
  navItems:    { display: 'flex', height: 60, alignItems: 'center' },
  navItem:     { padding: '0 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer', height: 60, display: 'flex', alignItems: 'center' },
  signOut:     { background: 'none', border: 'none', fontSize: 12, color: V.ink3, cursor: 'pointer' },
  breadBar:    { background: V.paper, borderBottom: `1px solid ${V.line}`, padding: '0 48px', height: 52, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  breadLink:   { fontSize: 13, color: V.ink4, cursor: 'pointer' },
  breadSep:    { fontSize: 13, color: V.line },
  breadCurrent:{ fontSize: 13, color: V.ink, fontWeight: 500 },
  body:        { padding: '32px 48px 80px', display: 'flex', flexDirection: 'column', gap: 20, overflowY: 'auto', flex: 1 },
  pageHead:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', paddingBottom: 20, borderBottom: `1px solid ${V.line}` },
  pageEyebrow: { fontSize: 9, fontWeight: 600, letterSpacing: 2.5, textTransform: 'uppercase', color: V.ink4, marginBottom: 8 },
  pageTitle:   { fontFamily: V.serif, fontSize: 32, fontWeight: 400, color: V.ink, lineHeight: 1.1, letterSpacing: -0.5 },
  pageSub:     { fontSize: 13, color: V.ink4, marginTop: 6 },
  loadWrap:    { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '60px 0' },
  insightBar:  { background: V.paper, border: `0.5px solid ${V.line}`, borderLeft: `3px solid ${V.ink2}`, borderRadius: 4, padding: '16px 20px', display: 'flex', gap: 16, alignItems: 'flex-start' },
  insightLeft: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, flexShrink: 0 },
  insightIcon: { width: 28, height: 28, background: V.paper2, border: `1px solid ${V.line}`, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: V.ink3 },
  insightLabel:{ fontSize: 8, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: V.ink4 },
  docCard:     { background: V.paper, border: `0.5px solid ${V.line}`, borderRadius: 5, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4 },
  pillBest:    { fontSize: 8, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: V.good, background: V.goodSoft, padding: '2px 7px', borderRadius: 3, alignSelf: 'flex-start', marginBottom: 6 },
  pillWorst:   { fontSize: 8, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: V.bad, background: V.badSoft, padding: '2px 7px', borderRadius: 3, alignSelf: 'flex-start', marginBottom: 6 },
  catTab:      { padding: '5px 14px', fontSize: 12, fontWeight: 400, border: `0.5px solid ${V.line}`, borderRadius: 20, background: 'none', color: V.ink3, cursor: 'pointer' },
  catTabOn:    { background: V.ink, borderColor: V.ink, color: V.paper, fontWeight: 500 },
  tableWrap:   { background: V.paper, border: `0.5px solid ${V.line}`, borderRadius: 5, overflow: 'hidden' },
  table:       { width: '100%', borderCollapse: 'collapse' },
  th:          { padding: '11px 14px', textAlign: 'left', fontSize: 9, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: V.ink4 },
  tdLabel:     { padding: '11px 14px', fontSize: 12, color: V.ink3, fontWeight: 500, borderRight: `0.5px solid ${V.line}`, background: V.paper2, minWidth: 180, verticalAlign: 'middle' },
  tdValue:     { padding: '11px 14px', fontSize: 13, borderRight: `0.5px solid ${V.line}`, verticalAlign: 'middle', fontFamily: V.sans },
  btnGhost:    { background: 'transparent', color: V.ink, border: `1px solid ${V.line}`, borderRadius: 4, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontFamily: V.sans, whiteSpace: 'nowrap' },
  btnGold:     { background: V.gold, color: V.ink, border: 'none', borderRadius: 4, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: V.sans },
};
